import { useCallback, useEffect, useState } from 'react';
import { getLotViewQuota } from '../../services/lotsService';
import {
  isLotViewQuotaExceeded,
  mergeLotViewQuotaFromLotResponse,
  parseLotViewQuotaFromStatusResult,
} from '../utils/lotViewQuota';

/**
 * Cupo diario de detalle catastral. Fuente de verdad: `GET /getlotviewquota`.
 * 403 de lote / parse / filtro oficial actualizan el mismo snapshot.
 *
 * @param {boolean} isLoggedIn
 * @see docs/maps/lot-view-quota.md
 */
export function useLotViewQuota(isLoggedIn) {
  const [lotViewQuota, setLotViewQuota] = useState(null);

  const refreshLotViewQuota = useCallback(async (signal) => {
    if (!isLoggedIn) return;
    const result = await getLotViewQuota({ signal });
    if (signal?.aborted) return;
    if (!result?._httpOk) return;
    const parsed = parseLotViewQuotaFromStatusResult(result);
    if (parsed) setLotViewQuota(parsed);
  }, [isLoggedIn]);

  const applyLotViewQuotaFromLot = useCallback((next) => {
    setLotViewQuota((prev) => mergeLotViewQuotaFromLotResponse(prev, next));
  }, []);

  useEffect(() => {
    if (!isLoggedIn) {
      setLotViewQuota(null);
      return undefined;
    }
    const controller = new AbortController();
    refreshLotViewQuota(controller.signal).catch((error) => {
      if (error?.name === 'AbortError') return;
      console.error('Error loading lot view quota:', error);
    });
    return () => controller.abort();
  }, [isLoggedIn, refreshLotViewQuota]);

  useEffect(() => {
    if (!isLoggedIn) return undefined;
    let controller = null;
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      controller?.abort();
      controller = new AbortController();
      refreshLotViewQuota(controller.signal).catch((error) => {
        if (error?.name === 'AbortError') return;
        console.error('Error refreshing lot view quota:', error);
      });
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      controller?.abort();
    };
  }, [isLoggedIn, refreshLotViewQuota]);

  return {
    lotViewQuota,
    lotViewQuotaExceeded: isLotViewQuotaExceeded(lotViewQuota),
    applyLotViewQuotaFromLot,
  };
}
